import React, { useCallback } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { MenuItemUnavailable } from 'features/menu/components/menu-item-unavailable';
import { ProductDetailCardModal } from 'features/menu/components/product-detail/product-detail-card-modal';
import { MenuObjectPage } from 'features/menu/pages/menu-object-page';
import { MenuProductPage } from 'features/menu/pages/menu-product-page/menu-product-page';
import { useStoreContext } from 'features/store';

import { CardModal } from 'components/card-modal';
import { MenuCatalogPreview } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-catalog-preview';
import { useOneMenuCatalog } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-one-menu-catalog';
import { useControlNoStoreStoreLocatorRedirect } from 'experiments/deeplinks-flow-feature/treatment/features/store/hooks/use-control-no-store-store-locator-redirect';
import {
  useDeeplinksFlowExperiment,
  useExposeDeeplinksFlowExperiment,
} from 'experiments/deeplinks-flow-feature/use-deeplinks-flow-experiment';
import { EntryTypeEnum } from 'generated/graphql-gateway';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { routes } from 'utils/routing';

const PREVIEWABLE_ENTRY_TYPES = new Set<string>([
  EntryTypeEnum.ITEM,
  EntryTypeEnum.COMBO,
  EntryTypeEnum.PICKER,
]);

export default function MenuObjectRoute() {
  const { noStoreSelected } = useStoreContext();
  const {
    isEnabled: isDeeplinksFlowEnabled,
    isLoading: isExperimentLoading,
  } = useDeeplinksFlowExperiment();

  useExposeDeeplinksFlowExperiment(noStoreSelected && !isExperimentLoading);

  if (noStoreSelected) {
    // Wait for Amplitude so treatment never flashes the control store locator.
    if (isExperimentLoading) {
      return (
        <CardModal alwaysShowHeader isLoading>
          {null}
        </CardModal>
      );
    }

    return isDeeplinksFlowEnabled ? (
      <MenuObjectNoStorePreview />
    ) : (
      <ControlMenuNoStoreStoreLocatorRedirect />
    );
  }

  return <MenuObjectInner />;
}

const MenuObjectInner = React.memo(() => {
  const enableUnifiedMenu = useEnableUnifiedMenu();

  return enableUnifiedMenu ? <MenuProductPage /> : <MenuObjectPage />;
});

/**
 * Control (deeplinks-flow-feature): skip the catalog info modal and open the
 * legacy full store locator, returning to this PDP after selection. If the guest
 * closes the locator without picking a store, dismiss this host so the
 * storeless empty state does not remain under a CardModal.
 */
function ControlMenuNoStoreStoreLocatorRedirect() {
  const { id = '', menuObjectType = '' } = useLocalSearchParams<{
    id: string;
    menuObjectType: string;
  }>();
  const redirectUrl = `${routes.menu}/${String(menuObjectType).toLowerCase()}/${id}`;

  useControlNoStoreStoreLocatorRedirect(redirectUrl);

  return (
    <CardModal alwaysShowHeader isLoading>
      {null}
    </CardModal>
  );
}

/**
 * Treatment no-store branch: item/combo/picker get catalog preview; other types
 * keep the legacy full store locator (same as control).
 */
function MenuObjectNoStorePreview() {
  const { menuObjectType = '' } = useLocalSearchParams<{
    id: string;
    menuObjectType: string;
  }>();

  const routeType = String(menuObjectType).toUpperCase();
  const routeTypeIsPreviewable = PREVIEWABLE_ENTRY_TYPES.has(routeType);

  // Sections and other non-previewable types stay on the legacy locator.
  if (menuObjectType && !routeTypeIsPreviewable) {
    return <ControlMenuNoStoreStoreLocatorRedirect />;
  }

  return <MenuObjectNoStorePreviewablePreview />;
}

/**
 * Treatment: no-store item/combo/picker catalog preview, then minimal store
 * locator on CTA. After store selection, redirectUrl returns here for the
 * store-scoped PDP.
 */
function MenuObjectNoStorePreviewablePreview() {
  const router = useRouter();
  const { id = '', menuObjectType = '' } = useLocalSearchParams<{
    id: string;
    menuObjectType: string;
  }>();

  const { findEntry, loading, error, ingredients, entries } = useOneMenuCatalog();
  const entry = id ? findEntry(id) : undefined;
  const entryTypeIsPreviewable =
    !!entry && PREVIEWABLE_ENTRY_TYPES.has(String(entry.type).toUpperCase());
  const redirectUrl = `${routes.menu}/${String(menuObjectType).toLowerCase()}/${id}`;

  const handleSelectRestaurant = useCallback(() => {
    router.navigate({
      pathname: routes.minimalStoreLocator,
      params: { redirectUrl },
    });
  }, [redirectUrl, router]);

  if (loading) {
    return (
      <CardModal alwaysShowHeader isLoading>
        {null}
      </CardModal>
    );
  }

  // Catalog miss/error for a previewable deeplink — no restaurant CTA here.
  if (error || !entry) {
    return (
      <ProductDetailCardModal alwaysHideHeader noDesktopWebMaxWidth>
        <MenuItemUnavailable isOffer={false} />
      </ProductDetailCardModal>
    );
  }

  // Entry resolved to a non-previewable type despite the route segment.
  if (!entryTypeIsPreviewable) {
    return <ControlMenuNoStoreStoreLocatorRedirect />;
  }

  return (
    <MenuCatalogPreview
      entry={entry}
      entries={entries}
      ingredients={ingredients}
      onSelectRestaurant={handleSelectRestaurant}
    />
  );
}
